import moment from 'moment';
import AppService from 'App/legacy/AppService';

const STORAGE_KEY = 'activityOutcomePostponePreset';

export type PostponePreference = {
	preset: string;
	timeOffsetMinutes?: number;
};

/**
 * Get the user-specific storage key
 */
const getStorageKey = (): string => {
	try {
		const self = AppService.getSelf();
		const customerId = AppService.getCustomerId();
		return `ls.${STORAGE_KEY}-${customerId}-${self?.id || ''}`;
	} catch {
		return `ls.${STORAGE_KEY}`;
	}
};

/**
 * Save the user's postpone preset preference to localStorage.
 * When 'currentDay' (later today) is selected with a specific time,
 * also stores the time offset in minutes from now.
 */
export const savePostponePreference = (preset: string, selectedTime?: Date | null): void => {
	try {
		const preference: PostponePreference = { preset };

		// If "later today" (currentDay) is selected with a specific time,
		// store the time offset in minutes from now
		if (preset === 'currentDay' && selectedTime) {
			const now = moment();
			const selected = moment(selectedTime);
			// Only store offset if it's today
			if (selected.isSame(now, 'day')) {
				const offsetMinutes = selected.diff(now, 'minutes');
				if (offsetMinutes > 0) {
					preference.timeOffsetMinutes = offsetMinutes;
				}
			}
		}

		localStorage.setItem(getStorageKey(), JSON.stringify(preference));
	} catch {
		// Silently fail if localStorage is not available
	}
};

/**
 * Get the saved postpone preset preference from localStorage.
 * Returns null if no preference is saved.
 */
export const getPostponePreference = (): PostponePreference | null => {
	try {
		const stored = localStorage.getItem(getStorageKey());
		if (stored) {
			return JSON.parse(stored) as PostponePreference;
		}
	} catch {
		// Silently fail if localStorage is not available or data is corrupted
	}
	return null;
};

/**
 * Get the default preset to use based on saved preference.
 * Falls back to the provided defaultPreset if no preference is saved.
 */
export const getDefaultPreset = (fallbackPreset: string): string => {
	const preference = getPostponePreference();
	return preference?.preset || fallbackPreset;
};

/**
 * Get the suggested time based on the saved preference.
 * If the user previously selected "later today" with a time offset,
 * this returns a Date object for the closest available hour slot.
 *
 * The available time options in the "Later today" dropdown are whole hours
 * starting from the next full hour after now. This function finds the
 * closest matching slot to `now + storedOffset`.
 *
 * Returns null if no time preference is saved or if it's not "later today".
 */
export const getSuggestedTimeFromPreference = (): Date | null => {
	const preference = getPostponePreference();

	if (!preference || preference.preset !== 'currentDay' || !preference.timeOffsetMinutes) {
		return null;
	}

	const now = moment();
	const endOfDay = now.clone().endOf('day');

	// Calculate the target time by adding the stored offset to now
	const targetTime = now.clone().add(preference.timeOffsetMinutes, 'minutes');

	// The dropdown shows whole-hour slots starting from the next full hour.
	// Round to the nearest full hour.
	let snappedTime = targetTime.clone().startOf('hour');
	if (targetTime.minute() >= 30) {
		snappedTime.add(1, 'hour');
	}

	// The first available slot is the next full hour after now
	const firstSlot = now.clone().startOf('hour').add(1, 'hour');

	// Ensure the snapped time is not before the first available slot
	if (snappedTime.isBefore(firstSlot)) {
		snappedTime = firstSlot;
	}

	// Make sure we don't go past end of day
	if (snappedTime.isAfter(endOfDay)) {
		return null;
	}

	return snappedTime.toDate();
};

/**
 * Clear the saved postpone preference from localStorage.
 */
export const clearPostponePreference = (): void => {
	try {
		localStorage.removeItem(getStorageKey());
	} catch {
		// Silently fail if localStorage is not available
	}
};
